import { useEffect, useRef, type RefObject } from 'react';

interface UseMenuPopupOptions {
  /** Whether the popup is shown. */
  open: boolean;
  /** Called on Escape. Read through a ref, so a new closure each render is fine. */
  onClose: () => void;
  /** The element that opens the popup: it gets focus back when the popup closes from inside. */
  triggerRef: RefObject<HTMLElement | null>;
  /** The popup's root, so the hook can tell whether focus was inside it. Without it only Escape restores focus. */
  popupRef?: RefObject<HTMLElement | null>;
}

/**
 * Keyboard + focus behaviour for a hand-built dropdown / popover (WCAG 2.1.1, 2.4.3).
 * While open: Escape calls `onClose` (unless something already handled the key or an IME is composing) and marks the key
 * as used (`preventDefault`), so one Escape closes one layer.
 * When the popup closes after Escape, or after focus was inside it (an item click, the popup's own close button),
 * the focused element is gone and focus would fall to <body>; it goes back to `triggerRef` instead.
 * A close by clicking elsewhere leaves focus where the user put it.
 */
export function useMenuPopup({ open, onClose, triggerRef, popupRef }: UseMenuPopupOptions) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    let focusWasInside = false;
    let escaped = false;
    const onFocusIn = (event: FocusEvent) => {
      const popup = popupRef?.current;
      focusWasInside = !!popup && event.target instanceof Node && popup.contains(event.target);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented || event.isComposing || event.keyCode === 229) return;
      escaped = true;
      event.preventDefault(); // this Escape is taken: an outer layer (a mobile drawer, a page shortcut) stands down
      onCloseRef.current();
    };
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('keydown', onKeyDown);
    // Seed from the current focus: the popup may already hold it when this effect runs.
    const popup = popupRef?.current;
    focusWasInside = !!popup && !!document.activeElement && popup.contains(document.activeElement);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('keydown', onKeyDown);
      if (!focusWasInside && !escaped) return;
      const active = document.activeElement;
      if (!active || active === document.body || !active.isConnected) triggerRef.current?.focus();
    };
  }, [open, popupRef, triggerRef]);
}
